import React, { useEffect, useState } from "react";
import Main from "../pages/Main";
import { exportTable } from "../utils/downloadExcel";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { fetchRechagesHistory } from "../api/rechages.Api";
import { autoDateFormat } from "../utils/autoDateFormate";
import { formatAmount } from "../utils/formatAmount";
import { useNotify } from "../context/NotifyContext";
import { useTxnDocActions } from "../hooks/useTxnDocActions";
import WalletHistoryBar from "../components/WalletHistoryBar";
import {
  ListingFilters,
  ListingPagination,
  SortableTh,
  CopyText,
  AppBreadcrumbs,
  RowActions,
  StatusBadge,
  useListingState,
  RECHARGE_STATUS_OPTIONS,
} from "../components/listing";

const RechargeWallet = () => {
  const { notify } = useNotify();
  const navigate = useNavigate();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const { category } = useParams();
  const service = params.get("service");
  const label = params.get("label");
  const label1 = params.get("label1");
  const listing = useListingState({ sortBy: "date", sortDir: "desc" });
  const { viewTxn, printTxn, pdfTxn } = useTxnDocActions("recharge");
  const [totalPages, setTotalPages] = useState(1);
  const [recharges, setRechagesData] = useState([]);
  const [totalLedgers, setTotalbyAll] = useState(0);
  const [totalAmount, setTotalAmount] = useState(0);

  const fetch_recharges = async () => {
    const apiParams = {
      ...listing.query,
      category: category,
    };
    try {
      const result = await fetchRechagesHistory(apiParams);
      if (result?.success) {
        const response = result?.data;
        setRechagesData(response?.utilityPayments || response?.recharge || []);
        setTotalPages(response?.pagination?.totalPages || 1);
        setTotalbyAll(response?.pagination?.total || 0);
        setTotalAmount(Number(response?.totalAmount) || 0);
      } else {
        notify(result?.message || "Server Error", "error");
      }
    } catch (error) {}
  };

  useEffect(() => {
    fetch_recharges();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    listing.page,
    listing.limit,
    listing.startDate,
    listing.endDate,
    listing.debouncedSearch,
    listing.sortBy,
    listing.sortDir,
    listing.status,
    category,
  ]);

  useEffect(() => {
    if (!category) {
      navigate(-1);
    }
  }, [category, navigate]);

  return (
    <Main showRightPanel={false}>
      <div className="mt-4">
        <AppBreadcrumbs
          items={[
            { label: "Dashboard", to: "/" },
            { label: label || "Recharge" },
            { label: "Payment History" },
            { label: label1 || "History" },
          ]}
        />

        <WalletHistoryBar title={service ? `${service} History` : "History"} to="/main-wallet-ledger" />

        <div className="ds-card text-sm">
          <ListingFilters
            onClear={listing.clearFilters}
            startDate={listing.startDate}
            endDate={listing.endDate}
            onStartDate={listing.setStartDate}
            onEndDate={listing.setEndDate}
            search={listing.search}
            onSearch={listing.setSearch}
            searchPlaceholder="Search"
            status={listing.status}
            onStatus={listing.setStatus}
            statusOptions={RECHARGE_STATUS_OPTIONS}
            showApply
            onApply={fetch_recharges}
            applyLabel="Load Records"
            onExport={() =>
              exportTable(
                "recharge-history",
                [
                  { label: "Date", value: (r) => (r?.createdAt ? autoDateFormat(r.createdAt) : "") },
                  {
                    label: "Ref Number",
                    value: (r) =>
                      r?.paysprintResponse?.refid ?? r?.paysprintResponse?.operatorid ?? "",
                  },
                  { label: "Transaction ID", key: "txnId" },
                  { label: "Ack Number", value: (r) => r?.paysprintResponse?.ackno ?? "" },
                  { label: "Amount", key: "amount" },
                  { label: "Status", key: "status" },
                  { label: "Message", value: (r) => r?.paysprintResponse?.message ?? "" },
                  { label: "Merchant", value: (r) => r?.userId?.userName ?? "" },
                ],
                recharges,
                notify
              )
            }
          />

          <div className="ds-table-wrap ds-scroll">
            <table className="ds-table ds-table-listing min-w-[980px]">
              <thead>
                <tr>
                  <SortableTh
                    label="Date"
                    sortKey="date"
                    activeKey={listing.sortBy}
                    direction={listing.sortDir}
                    onSort={listing.toggleSort}
                  />
                  <th>Ref.Number</th>
                  <th>Transaction ID</th>
                  <th>Ack. Number</th>
                  <SortableTh
                    label="Trans. Amount"
                    sortKey="amount"
                    activeKey={listing.sortBy}
                    direction={listing.sortDir}
                    onSort={listing.toggleSort}
                  />
                  <SortableTh
                    label="Status"
                    sortKey="status"
                    activeKey={listing.sortBy}
                    direction={listing.sortDir}
                    onSort={listing.toggleSort}
                  />
                  <th>Message</th>
                  <th>Merchant</th>
                  <th className="ds-th-actions">Actions</th>
                </tr>
              </thead>
              <tbody>
                {recharges?.length === 0 ? (
                  <tr>
                    <td colSpan="9" className="text-center text-[var(--text-muted)]">
                      NO DATA AVAILABLE IN TABLE
                    </td>
                  </tr>
                ) : (
                  recharges?.map((row, index) => (
                    <tr key={row?._id || index}>
                      <td>{row?.createdAt ? autoDateFormat(row.createdAt) : "--"}</td>
                      <td>
                        <CopyText
                          value={
                            row?.paysprintResponse?.refid ?? row?.paysprintResponse?.operatorid
                          }
                          label="Ref Number"
                        />
                      </td>
                      <td>
                        <CopyText value={row?.txnId} label="Txn ID" />
                      </td>
                      <td>{row?.paysprintResponse?.ackno ?? "--"}</td>
                      <td className="ds-td-amount">₹ {formatAmount(row?.amount) ?? "--"}</td>
                      <td>
                        <StatusBadge status={row?.status} />
                      </td>
                      <td>{row?.paysprintResponse?.message ?? "--"}</td>
                      <td>{row?.userId?.userName ?? "--"}</td>
                      <td>
                        <RowActions
                          onView={() => viewTxn(row)}
                          onPdf={() => pdfTxn(row)}
                          onPrint={() => printTxn(row)}
                        />
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
              <tfoot>
                <tr className="ds-table-total">
                  <td colSpan={4} className="ds-table-total__label">
                    TOTAL Recharge Amount
                  </td>
                  <td className="ds-table-total__value">₹ {formatAmount(totalAmount)}</td>
                  <td colSpan={4}></td>
                </tr>
              </tfoot>
            </table>
          </div>

          <ListingPagination
            page={listing.page}
            limit={listing.limit}
            total={totalLedgers}
            totalPages={totalPages}
            onPageChange={listing.setPage}
            onLimit={listing.setLimit}
            limitOptions={[20, 50, 100]}
          />
        </div>
      </div>
    </Main>
  );
};

export default RechargeWallet;
